import { Injectable, inject } from '@angular/core';
import { Observable, catchError, exhaustMap, filter, map, merge, of, timer } from 'rxjs';
import { ConnectivityService } from '@app/core/connectivity/connectivity.service';
import { isSessionExpired } from '@app/core/http/http-error';
import { TokenStatus } from '@app/core/session/onboarding-session.model';
import { SessionEndReason } from '@app/core/session/session-end-reason';
import { SESSION_TIMING } from '@app/core/session/session-timing';
import { TokenStatusApi } from './data/token-status.api';

const REASON_BY_STATUS: Readonly<Partial<Record<TokenStatus, SessionEndReason>>> = {
  expired: 'expired',
  completed: 'completed',
};

/**
 * Consulta el estado del token mientras la sesión está activa, para sacar al cliente si venció o si alguien completó
 * el proceso (varias personas pueden tener abierto el mismo token). Es experiencia, no seguridad: el backend tiene que
 * rechazar cualquier escritura sobre un token que ya no está en curso.
 * No conoce al detector de actividad: consultar nunca reinicia el contador de inactividad.
 */
@Injectable({ providedIn: 'root' })
export class SessionStatusPoller {
  private readonly api = inject(TokenStatusApi);
  private readonly connectivity = inject(ConnectivityService);

  /**
   * Consulta cada `pollIntervalMs` (la primera a los 60 s: el estado se acaba de validar) y en cada `wakeUp$`.
   * Emite solo cuando el token dejó de estar en curso.
   */
  watch(token: string, wakeUp$: Observable<unknown>): Observable<SessionEndReason> {
    const every = SESSION_TIMING.pollIntervalMs;
    return merge(timer(every, every), wakeUp$).pipe(
      // Sin red no tiene sentido consultar: se reintenta en el próximo tick.
      filter(() => this.connectivity.isOnline()),
      // exhaustMap y no switchMap: el tick que llega con una consulta en vuelo se descarta. switchMap la cancelaría,
      // y con un backend lento ninguna consulta llegaría a terminar.
      exhaustMap(() => this.check(token)),
      filter((reason) => reason !== null),
    );
  }

  /** Red caída, timeout, 5xx o un status desconocido: `null`, la sesión sigue. Solo un 401/410 equivale a vencido. */
  private check(token: string): Observable<SessionEndReason | null> {
    return this.api.pollStatus(token).pipe(
      map((status) => (status ? (REASON_BY_STATUS[status] ?? null) : null)),
      catchError((error: unknown) => of<SessionEndReason | null>(isSessionExpired(error) ? 'expired' : null)),
    );
  }
}
